import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle, Plug, Link as LinkIcon, ArrowsClockwise, Sliders, Lightning } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { GlassCard } from "@/components/glass/GlassCard";
import { PageShell } from "@/components/layout/AppLayout";
import IntegrationConfigDialog from "@/components/IntegrationConfigDialog";
import SearchSortBar from "@/components/SearchSortBar";
import useSearchSort from "@/hooks/useSearchSort";
import api from "@/lib/api";
import { toast } from "sonner";

const CATEGORY_COLORS = {
  crm: "from-sky-500 to-cyan-400",
  erp: "from-emerald-500 to-teal-400",
  signature: "from-amber-500 to-orange-400",
  enrichment: "from-fuchsia-500 to-pink-400",
  compliance: "from-rose-500 to-red-400",
  notifications: "from-violet-500 to-purple-400",
};

export default function Integrations() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = () => api.get("/integrations").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
    // Ensure Resend + Twilio exist in the integration list for this tenant
    (async () => {
      const { data } = await api.get("/integrations");
      const names = new Set(data.map((x) => x.provider));
      for (const p of ["Resend", "Twilio"]) {
        if (!names.has(p)) {
          // create as available (via simple "integrations/seed-extras" — but we don't have that; skip silently)
        }
      }
    })();
  }, []);

  const cats = ["all", ...Array.from(new Set(items.map((i) => i.category)))];
  const scoped = tab === "all" ? items : items.filter((i) => i.category === tab);
  const s = useSearchSort(scoped, {
    searchKeys: ["provider", "category"],
    sortOptions: [
      { value: "provider", label: "Name A→Z", defaultDir: "asc" },
      { value: "category", label: "Category", defaultDir: "asc" },
      { value: "status", label: "Status", defaultDir: "asc" },
      { value: "last_sync", label: "Last synced", defaultDir: "desc" },
    ],
  });
  const filtered = s.items;

  return (
    <PageShell
      title="Integrations"
      subtitle="ERP, CRM, e-signature, notifications & intelligence sources"
    >
      <SearchSortBar controller={s} placeholder="Search integrations by name or category…" testIdPrefix="integrations" />
      <div className="flex gap-2 mb-6 flex-wrap">
        {cats.map((c) => (
          <button
            key={c}
            onClick={() => setTab(c)}
            data-testid={`integrations-tab-${c}`}
            className={`px-3 py-1.5 rounded-md text-sm border transition ${tab === c ? "bg-[var(--surface-strong)] border-[var(--border-strong)]" : "bg-[var(--surface)] border-[var(--border)] text-[var(--text-2)] hover:text-[var(--text)]"}`}
          >
            {c.toUpperCase()}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((i, idx) => (
          <motion.div key={i.id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.03 }}>
            <GlassCard hover className="p-6 h-full flex flex-col">
              <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 rounded-md bg-gradient-to-br ${CATEGORY_COLORS[i.category] || "from-slate-500 to-slate-600"} flex items-center justify-center`}>
                  <Plug size={18} weight="fill" className="text-white" />
                </div>
                {i.status === "connected" ? (
                  <span className="chip chip-low inline-flex items-center gap-1"><CheckCircle size={10} weight="fill" /> connected</span>
                ) : (
                  <span className="chip chip-neutral">available</span>
                )}
              </div>
              <div className="flex-1">
                <div className="text-lg font-medium mb-1" style={{ fontFamily: 'var(--font-heading)' }}>{i.provider}</div>
                <div className="overline">{i.category}</div>
                {i.last_test && (
                  <div className="mt-3 text-[10px] font-mono text-[var(--text-3)] flex items-center gap-1">
                    {i.last_test.ok ? <CheckCircle size={10} weight="fill" className="text-emerald-400" /> : <Lightning size={10} className="text-rose-400" />}
                    Last test: {i.last_test.ok ? "✓" : "✗"} {i.last_test.latency_ms}ms · {new Date(i.last_test.tested_at).toLocaleDateString()}
                  </div>
                )}
              </div>
              <div className="mt-5 flex items-center justify-between text-xs text-[var(--text-3)]">
                {i.last_sync && (
                  <span className="flex items-center gap-1"><ArrowsClockwise size={10} /> {new Date(i.last_sync).toLocaleDateString()}</span>
                )}
                <Button
                  size="sm"
                  onClick={() => setSelected(i)}
                  data-testid={`integration-configure-${i.id}`}
                  className="gap-1 ms-auto"
                >
                  <Sliders size={12} /> Configure
                </Button>
              </div>
            </GlassCard>
          </motion.div>
        ))}
      </div>

      <IntegrationConfigDialog
        integration={selected}
        open={!!selected}
        onOpenChange={(o) => !o && setSelected(null)}
        onSaved={load}
      />
    </PageShell>
  );
}
